// Course Content Data - "Taking Your Power Back" E-Course
// Presented by Breah Kettunen - The Magnetic Mind Method

export const courseInfo = {
  title: "Taking Your Power Back",
  subtitle: "The 5-Step Magnetic Mind Method to Consciously Create Your Life",
  author: "Presented by Breah Kettunen",
  description: "Learn how to Magically Manifest and become the Conscious Creator of your own life using the Magnetic Mind Method. Stop feeling stuck, overwhelmed, and unfulfilled—unlock your personal power and lift the lid off your limitations.",
  totalChapters: 5,
  estimatedTime: "5-7 hours",
  heroImage: "https://customer-assets.emergentagent.com/job_authentic-creation/artifacts/psoq4m6s_Taking%20Your%20Power%20Back_20260211_213904_0000.png",
};

export const courseIntro = {
  title: "This Course is for You if...",
  points: [
    "You would like to learn how to Magically Manifest and become the Conscious Creator of your own life",
    "You're feeling stuck, overwhelmed, unfulfilled, sick of living with the internal struggles you have and perhaps not really knowing what it is you truly desire",
    "You know you haven't reached your highest potential and can't seem to lift the lid off your limitations that are holding you back",
    "You know you can become the person you've always wanted to be, but you just can't seem to get there",
  ],
  whatYouWillLearn: [
    "How what you have in your life is a direct reflection of your unconscious conditioning",
    "How to unlock your personal power using the 5-Step Magnetic Mind Method to magically manifest and become the Conscious Creator of your own life",
    "How to shift your resistance and limitations easily and effortlessly",
  ],
};

export const fourPillars = [
  {
    number: 1,
    title: "True Choices",
    description: "Know what you truly desire",
  },
  {
    number: 2,
    title: "Unconscious Egoic Agenda",
    description: "Understand how what you have in your life is a direct reflection of your unconscious conditioning",
  },
  {
    number: 3,
    title: "Resistance",
    description: "What it is and how to remove it with ease",
  },
  {
    number: 4,
    title: "Take Aligned Action",
    description: "Our behaviour is the highest form of communication",
  },
];

export const chapters = [
  {
    id: 1,
    step: "Step 1",
    title: "Choose a True Goal",
    subtitle: "Learn How to Align Your Target with Your End Result",
    duration: "50 min read",
    audioLength: "35 min",
    image: "https://images.pexels.com/photos/2041397/pexels-photo-2041397.jpeg?auto=compress&cs=tinysrgb&w=800",
    summary: "Learn how to align your target with your end result and move forward with complete clarity. Discover the 7 Focus Points that keep you from making True Choices and how to break free from them.",
    content: `
## Choose a True Goal

The first step in the Magnetic Mind Method is choosing a **True Goal**—one that is genuinely aligned with what you would love, not what you've been conditioned to want.

Most people don't actually have True Goals. They have conditioned responses, reactions to circumstances, or watered-down versions of their real dreams. This is why they stay stuck, frustrated, and unfulfilled despite their best efforts.

> "You can't plant seeds of scarcity and expect to get trees of abundance." — Christopher M Duncan

---

### Journal Prompt: Reflect on Your Current Goals

*Take a moment to write freely. What goals are you currently pursuing? Are they truly what YOU want, or what you think you should want?*

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

---

### The 4 Pillars You Need to Know

Before we dive into True Choices, understand that there are 4 Pillars that form the foundation of conscious creation:

**Pillar 1: True Choices** - Know what you truly desire

**Pillar 2: Unconscious Egoic Agenda** - Understand how what you have in your life is a direct reflection of your unconscious conditioning

**Pillar 3: Resistance** - What it is and how to remove it with ease

**Pillar 4: Take Aligned Action** - Our behaviour is the highest form of communication

> "The action you take, is the highest form of communication." — Breah Kettunen

### The 7 Focus Points That Keep You From a True Choice

Most people aren't making True Choices. They're stuck in one of these 7 Focus Points that keep them from ever creating what they actually want:

**1. Resolve a Negative Belief**
Your goal and action is about completing a way you feel incomplete. You're trying to prove something to yourself or others, rather than creating from genuine desire.

*Example: "I need to make a million dollars to prove I'm not a failure."*

**2. Reaction**
Your goal is actually a desire to get away from an unwanted circumstance or condition. You're running FROM something, not moving TOWARD something you love.

*Example: "I want to lose weight because I hate how I look."*

**3. Limitation**
Your goal is actually focused on what you think is possible for you, not what you would really love. You have edited your dream to fit your current beliefs about yourself.

*Example: "I guess I'd be happy with just a small business, even though I dream of a global impact."*

**4. Others' Opinions**
Your goal is designed to ensure others are happy. You're people-pleasing rather than pursuing your authentic desires.

*Example: "I should become a doctor because my parents expect it."*

**5. Control**
Here your goals must show up in the exact way you want them. You are not open to the end result showing up differently (or better) than you imagined.

*Example: "The only way I'll be successful is if THIS specific plan works out exactly as I planned."*

**6. Indirectness**
Your goals are the vehicles you have created to get you somewhere else. You are taking the long route instead of going directly for what you want.

*Example: "If I get this degree, then I can get this job, then I can save money, then I can do what I really want..."*

**7. Default**
This person doesn't really have goals and thinks what they have is 'enough'. They've given up on dreaming and settled for mediocrity.

*Example: "This is fine. I shouldn't want more. Who am I to dream big?"*

---

### Journal Prompt: Identify Your Focus Point

*Which of these 7 Focus Points resonates most with where you've been stuck? Write about a specific goal you've had that fell into one of these categories.*

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

---

### Making a True Choice

A True Choice is:
- Based on what you would LOVE, not what you think you can have
- Independent of circumstances
- Not a reaction to something you don't want
- Free from the need to prove anything
- Aligned with your deepest values and authentic self
- Open to HOW it shows up

> "When you make a True Choice, you align with your true nature and purpose. You become the predominant creative force in your life." — Breah Kettunen

### The Clarity Exercise

Ask yourself these questions and be completely honest:

1. If no one would ever judge you, what would you create with your life?
2. What would you pursue if you knew—with absolute certainty—that you couldn't fail?
3. What dream have you edited down because you didn't think it was possible?
4. What are you pursuing because someone else wants it for you?
5. What are you avoiding that you actually deeply desire?

---

### Journal Space: Your Clarity Answers

*Answer each question from The Clarity Exercise below. Be honest with yourself—no one else will read this.*

**1. If no one would judge me, I would create:**

_______________________________________________

_______________________________________________

**2. If I couldn't fail, I would pursue:**

_______________________________________________

_______________________________________________

**3. The dream I've edited down is:**

_______________________________________________

_______________________________________________

**4. I'm pursuing this for someone else:**

_______________________________________________

_______________________________________________

**5. What I'm avoiding but deeply desire:**

_______________________________________________

_______________________________________________

---

### Identifying Your True Goal

Your True Goal should make you feel:
- Excited AND a little scared
- Expanded, not contracted
- Called forward, not pushed
- Aligned with your deepest self
- Free, not obligated

> "Commit your way to the All, to Source; trust in Him, and He will act." (Psalm 37:5)

Write it in present tense:
"I am now living a life where I _______."

Don't worry about the HOW yet. Just get clear on the WHAT.

### Common Mistakes in Goal Setting

**THE PROBLEM:** You have been trying to create an outcome for years and you still don't have it.

**THE SOLUTION:** Learn the alchemy of creation and shift your identity through the 5-step process.

**THE MISTAKES:**
- Working from the wrong structure
- Giving your power away
- Trying to heal the current reality, instead you must let go
- Thinking you will change when you achieve a result (you must BE it before you SEE it)
- Out of emotional alignment with what you desire
- Not following natural laws
- Not going for what you love
- Not following through
- Holding on to the past as though it is real

> "But seek first the kingdom of Source and his righteousness, and all these things will be added to you." (Matthew 6:33)

---

### Journal Prompt: Your True Goal Statement

*Complete this statement with what you would truly LOVE. Write it in present tense, as if it's already happening:*

**"My True Goal is to:**

_______________________________________________

_______________________________________________

_______________________________________________

**because this is what I would genuinely love to experience, not because I'm trying to prove anything, escape something, or please anyone else."**

---

### Notes & Personal Insights

*Use this space to capture any thoughts, insights, or breakthroughs from this chapter:*

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

---

This is your starting point. Everything else builds from here.
    `,
    reflection: {
      prompt: "Which of the 7 Focus Points have been keeping you from a True Choice? Write out what you've been pursuing that ISN'T a True Goal, and then write what your actual True Goal would be if you gave yourself full permission to want it.",
      placeholder: "I've been stuck in Focus Point #___ because... My actual True Goal is...",
    },
    keyTakeaways: [
      "Most goals aren't True Goals—they're reactions, limitations, or attempts to please others",
      "The 7 Focus Points reveal where you might be stuck in false goal-setting",
      "A True Goal is based on what you'd LOVE, not what you think you can have",
      "You must BE it before you SEE it—identity comes before results",
    ],
  },
  {
    id: 2,
    step: "Step 2",
    title: "Create Structural Tension",
    subtitle: "Pull Yourself Toward Your Desire Without Internal Struggle",
    duration: "55 min read",
    audioLength: "40 min",
    image: "https://static.prod-images.emergentagent.com/jobs/1900c591-748a-4a5a-b5a8-5b3f7fcca452/images/479a0311f364edefdd5343d37adacf521357190910ef4c94de604b03db66b224.png",
    summary: "Learn how to pull yourself towards what you desire without internal struggle and with minimal effort. Understand the Three Structures—Stuck, Oscillating, and Creative—and discover which one you're operating from.",
    content: `
## Create Structural Tension

The second step in the Magnetic Mind Method is creating **Structural Tension**—the natural force that pulls you toward your True Goal without the exhausting struggle most people experience.

Most people are working from the wrong structure entirely. They're creating tension that pushes them AWAY from what they want instead of pulling them toward it.

> "The action you take, is the highest form of communication." — Breah Kettunen

---

### Journal Prompt: Your Current Structure

*Before we dive in, reflect on your current patterns. Do you find yourself starting strong then falling off? Getting close to success then sabotaging? Write about a recent example.*

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

---

### Understanding the Unconscious Mind

Before we can create proper structural tension, we need to understand what we're working with.

**What is the Unconscious?**

The unconscious is:
- The part of us that is running the body
- The domain of feelings and emotions
- Trainable and can learn/automate processes (like brushing your teeth, speaking, writing)
- Designed to keep you alive (it has no other goal than to keep you safe)
- Learns how to "be" at an early age and doesn't want to change

This is crucial: **Your unconscious mind learned how to be when you were very young, and its primary goal is to keep you safe.** It perceives change—even positive change—as a potential threat.

> "But seek first the kingdom of Source and his righteousness, and all these things will be added to you." (Matthew 6:33)

### The 3 Levels of Consciousness

Understanding these three levels is key to unlocking your creative power:

**1. Superconscious → Source → Infinite**
- Connected to Source/Universal Intelligence
- Infinite potential and possibility
- Where true creation originates
- Access to unlimited wisdom and guidance
- The realm of inspiration, intuition, and higher knowing
- This is where you tap into when you become a conscious creator

**2. Unconscious → Soul → Limited**
- The domain of the Soul
- Contains your programming, beliefs, and patterns
- Limited by past experiences and conditioning
- Runs automatic behaviors (designed to keep you safe)
- Where your Unconscious Egoic Agenda lives
- Can be reprogrammed through the Recode process

**3. Self-Conscious → Ego → Polarity**
- The realm of the Ego
- Experiences life through polarity (good/bad, right/wrong, success/failure)
- Creates separation and judgment
- Focused on survival, comparison, and protection
- Often keeps you stuck in the Oscillating or Stuck Structure
- The voice that says "you can't" or "you're not enough"

**The Key Insight:**

Most people operate primarily from the Self-Conscious (Ego) level, reacting to life through polarity and limitation. The Magnetic Mind Method teaches you to:

1. **Recognize** when you're operating from Ego (polarity/limitation)
2. **Release** the limitations stored in the Unconscious (through Recoding)
3. **Access** the Superconscious (Source/Infinite) to consciously create

When you learn to tap into the Superconscious while reprogramming the Unconscious, you become the predominant creative force in your life—no longer limited by ego-driven polarity.

> "Commit your way to the All, to Source; trust in Him, and He will act." (Psalm 37:5)

---

### Journal Prompt: Your Levels of Consciousness

*Which level do you find yourself operating from most often? The Superconscious (inspiration, intuition), Unconscious (automatic patterns), or Self-Conscious (ego, judgment)? Write about a recent situation where you noticed which level you were in.*

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

---

### The 9 Unconscious Egoic Agendas

Your unconscious has adopted one or more of these core beliefs that drive your behavior. Recognizing yours is essential to creating change:

**1. "I am unworthy"**
I deny my own needs and only feel valid by helping others.

**2. "I am not good enough"**
I need to achieve external success to be "valid."

**3. "I don't belong/not accepted"**
I must become accepted and never rejected. I am searching for love.

**4. "I am not safe/the world is not safe"**
I must retreat to my mind and understand it all before I get hurt.

**5. "I am not able to handle negative emotion"**
I will avoid negative emotions at all costs.

**6. "I/the world is not perfect"**
There is a way to be perfect, and achieving this is more important than anything.

**7. "I don't have permission"**
I need an authority to tell me what to do.

**8. "I am not able to handle conflict"**
I will have no goals (to avoid any potential conflict).

**9. "I am powerless"**
I must have control and power to be successful.

---

### Journal Prompt: Your Unconscious Egoic Agenda

*Which of the 9 Unconscious Egoic Agendas resonates most deeply with you? Don't overthink it—your first instinct is usually correct. Write about how this agenda has shown up in your life.*

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

---

> "You can't plant seeds of scarcity and expect to get trees of abundance." — Christopher M Duncan

### The Three Structures

This is one of the most important concepts in the Magnetic Mind Method. There are THREE structures that determine whether you create what you want or stay stuck:

**Structure 1: The Stuck Structure**

In this structure:
1. You have an Unconscious Egoic Belief (like "I'm not good enough" or "I don't deserve success")
2. This belief creates a ceiling on what you allow yourself to have
3. You don't take meaningful action toward your goals
4. You stay in your comfort zone, even if it's uncomfortable
5. You feel paralyzed, unable to move forward
6. Dreams remain dreams—nothing changes

People in the Stuck Structure often feel:
- Frozen and unable to take action
- Hopeless about ever changing
- Like they're watching life pass them by
- Resigned to their current circumstances
- Deeply unfulfilled but unable to do anything about it

**This is where people give up before they even start.**

**Structure 2: The Oscillating Structure**

In this structure:

1. You have an Unconscious Egoic Belief (like "I'm not good enough")
2. You take action toward a goal
3. The action creates TENSION with your belief
4. The tension becomes uncomfortable
5. You take action to RESOLVE the tension (self-sabotage, quitting, etc.)
6. You return to your "normal" state
7. Cycle repeats

This is why people yo-yo diet, start businesses then quit, get close to success then fall back. **They're working from a structure designed to maintain the status quo.**

People in the Oscillating Structure experience:
- Starting strong then falling off
- Getting close to success then sabotaging
- Feeling like they're on a hamster wheel
- Making progress then losing it all
- Constant ups and downs with no lasting change

**This is where most people are stuck—forever oscillating back to their baseline.**

**Structure 3: The Creative Structure (Where You Need to Be)**

In this structure:

1. You have a clear True Goal (end result)
2. You acknowledge your Current Reality honestly
3. The GAP between them creates Structural Tension
4. This tension naturally resolves toward the True Goal
5. Actions feel inspired, not forced
6. Progress compounds and is lasting

People in the Creative Structure experience:
- Being naturally pulled toward their goals
- Taking inspired action without forcing
- Lasting results that compound over time
- Flow instead of struggle
- Becoming the person who has what they desire

**This is where conscious creators operate—and where you're headed.**

### The Key Difference Between the Three Structures

| Stuck Structure | Oscillating Structure | Creative Structure |
|-----------------|----------------------|-------------------|
| No action | Action then sabotage | Aligned action |
| Paralysis | Yo-yo pattern | Steady progress |
| Dreams stay dreams | Temporary results | Lasting transformation |
| Ceiling on possibility | Return to baseline | Continuous expansion |
| Resignation | Frustration | Fulfillment |

### Why the Structure Matters

When you're in the Stuck Structure:
- You're not even in the game
- Dreams feel impossible
- You've given up before starting
- Life happens TO you

When you're in the Oscillating Structure:
- You're essentially running in place
- Success feels like pushing a boulder uphill
- You experience constant internal struggle
- Results are temporary before "snapping back"

When you're in the Creative Structure:
- You're naturally pulled toward your goal
- Success feels like flowing downstream
- Internal struggle dissolves
- Results are lasting and compound over time

### How Action Creates Tension

In the Stuck Structure:
- Action feels impossible or pointless
- The unconscious belief completely blocks forward movement
- Example: "Why bother trying? It won't work anyway."

In the Oscillating Structure:
- Action creates tension WITH your unconscious belief
- Example: Making money creates tension with "I'm not worthy of wealth"
- Uncomfortable tension leads to self-sabotage to relieve it

In the Creative Structure:
- The gap between Current Reality and True Goal creates tension
- This tension resolves toward the vision
- Actions are pulled forward by the goal, not pushed by pain

### Which Structure Are You In?

**You're in the STUCK Structure if:**
- You've stopped dreaming or setting goals
- You feel paralyzed and unable to take action
- You've resigned yourself to "this is just how life is"
- You watch others succeed and feel it's not possible for you

**You're in the OSCILLATING Structure if:**
- You start things with enthusiasm but don't finish
- You've achieved success then lost it (multiple times)
- You feel like you're on a never-ending hamster wheel
- You experience cycles of hope followed by disappointment

**You're in the CREATIVE Structure if:**
- You feel pulled toward your goals naturally
- Your progress compounds over time
- You experience flow more than struggle
- Results feel lasting and sustainable

---

### Journal Prompt: Identify Your Structure

*Based on the descriptions above, which structure are you currently operating from? Be honest with yourself. Write about specific patterns in your life that reveal your current structure.*

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

---

### Moving from Stuck or Oscillating to Creative

The good news: **You can shift structures.** That's exactly what the Magnetic Mind Method teaches you to do.

The shift requires:
1. Awareness of which structure you're currently in
2. Understanding WHY you're in that structure (Unconscious Egoic Agenda)
3. Creating proper Structural Tension (not tension with your beliefs)
4. Aligning your emotional state with your True Goal
5. Taking aligned action from your new identity

### Creating Proper Structural Tension

**Step 1: Get Clear on Your True Goal (from Step 1)**

Write it in present tense, as if it's already your reality.

**Step 2: Honestly Assess Your Current Reality**

Where are you NOW in relation to your goal? Be completely honest—no delusion, no drama.

**Step 3: Hold Both Simultaneously**

Don't escape into your vision to avoid current reality. Don't wallow in current reality and forget your vision. Hold BOTH clearly at the same time.

**Step 4: Let the Tension Work FOR You**

When you hold both clearly, you'll feel a natural pull toward your True Goal. This isn't willpower—it's structural.

**Step 5: Ask: "What Would Resolve This Tension?"**

The right actions will become obvious when the tension is properly established.

> "You cannot grow an abundance tree by planting seeds of scarcity. You must be in emotional alignment with what you desire." — Breah Kettunen

---

### Journal Space: Your Structural Tension

*Use this space to map out your structural tension:*

**My True Goal (present tense):**

_______________________________________________

_______________________________________________

**My Current Reality (honest assessment):**

_______________________________________________

_______________________________________________

**The Gap I'm holding:**

_______________________________________________

_______________________________________________

**What actions feel obvious to resolve this tension:**

_______________________________________________

_______________________________________________

---

### Notes & Personal Insights

*Capture any breakthroughs or realizations from this chapter:*

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

---
    `,
    reflection: {
      prompt: "Which of the Three Structures are you currently in—Stuck, Oscillating, or Creative? Which of the 9 Unconscious Egoic Agendas resonates most with you? Describe both your True Goal and your honest Current Reality to begin creating proper Structural Tension.",
      placeholder: "I believe I'm in the ___ Structure because... My Unconscious Egoic Agenda is... My True Goal is... My Current Reality is...",
    },
    keyTakeaways: [
      "There are Three Structures: Stuck (paralysis), Oscillating (yo-yo), and Creative (lasting progress)",
      "Your unconscious mind's primary goal is to keep you safe—even from positive change",
      "The 9 Unconscious Egoic Agendas reveal the beliefs keeping you stuck or oscillating",
      "The Creative Structure uses the gap between vision and reality to pull you forward naturally",
    ],
  },
  {
    id: 3,
    step: "Step 3",
    title: "Emotion of the End Result",
    subtitle: "Create NEW Emotional Set Points",
    duration: "55 min read",
    audioLength: "42 min",
    image: "https://images.unsplash.com/photo-1763139695210-0efe4cdcb7dc?w=800&auto=format&fit=crop&q=80",
    summary: "Learn how to create NEW emotional set points based on the person you want to be rather than the person you need to fix. This is where you align your emotional state with your True Goal.",
    content: `
## Emotion of the End Result

The third step in the Magnetic Mind Method is creating **NEW emotional set points** based on the person you WANT TO BE, rather than constantly trying to fix the person you think you are.

This is perhaps the most transformative step because it addresses a fundamental mistake most people make: **They think they will feel different when they achieve their goal. The truth is, they need to feel different FIRST.**

> "Commit your way to the All, to Source; trust in Him, and He will act." (Psalm 37:5)

### The Big Mistake

Most people approach transformation like this:
1. "When I have more money, I'll feel secure."
2. "When I lose weight, I'll feel confident."
3. "When I find love, I'll feel worthy."
4. "When I achieve success, I'll feel enough."

**This is backwards.**

The truth: You must BE it before you SEE it.

Your emotional state is not the RESULT of your circumstances—your circumstances are the RESULT of your emotional state.

---

### Journal Prompt: Your "When/Then" Beliefs

*What "when/then" beliefs have you been holding? "When I achieve ____, then I'll feel ____." Write them down so you can see how you've been approaching transformation backwards.*

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

---

### What Are Emotional Set Points?

Think of your emotional set point like a thermostat. Your nervous system has been programmed to return to certain emotional states—your "normal."

For many people, especially those with the Unconscious Egoic Agendas we discussed in Step 2, this set point includes:
- Low-level anxiety
- Unworthiness
- Not-enoughness
- Hypervigilance
- Guilt
- Fear

No matter what happens externally, you return to this emotional baseline. Win the lottery? After the initial excitement, you return to baseline. Get the promotion? Back to baseline. Find a great relationship? Back to baseline.

**This is why external achievements don't create lasting happiness.**

> "But seek first the kingdom of Source and his righteousness, and all these things will be added to you." (Matthew 6:33)

### You Cannot Grow an Abundance Tree with Seeds of Scarcity

Here's the critical insight:

> "You can't plant seeds of scarcity and expect to get trees of abundance." — Christopher M Duncan

If your emotional set point is scarcity, fear, and unworthiness, you cannot create lasting abundance. You might get temporary results, but you'll eventually return to your emotional baseline—and your circumstances will follow.

**Your emotional state is the SEED. Your circumstances are the FRUIT.**

You cannot plant seeds of:
- "I'm not good enough"
- "I don't deserve this"
- "Something will go wrong"
- "I can't handle this"

And expect to harvest:
- Success
- Abundance
- Love
- Freedom

### Creating New Emotional Set Points

The key is not to FIX your current emotional state but to CREATE a new one based on the person you want to be.

**Step 1: Identify Your Current Emotional Set Point**

What emotions do you experience most often?
- What do you feel when you wake up?
- What is the background emotional hum of your life?
- What feelings feel "normal" to you (even if uncomfortable)?

**Step 2: Identify Your Desired Emotional Set Point**

If you were living your True Goal right now:
- How would you feel when you wake up?
- What would your background emotional hum be?
- What emotions would feel "normal"?

**Step 3: Practice the NEW Emotional State**

This is not positive thinking or fake affirmations. This is genuine emotional practice.

The person who has achieved your True Goal:
- What do they feel as they move through their day?
- How do they respond to challenges?
- What's their baseline emotional state?

**Practice being that person NOW.** Not when you "earn it." Now.

### The Difference: Fixing vs. Creating

**Fixing says:** "Something is wrong with me. I need to find it and correct it."

**Creating says:** "I am creating a new emotional reality based on who I choose to be."

When you try to FIX yourself:
- You reinforce that something is wrong
- You stay focused on problems
- You keep the old identity alive
- Change is a constant struggle

When you CREATE a new emotional set point:
- You focus on who you're becoming
- You align with your vision
- You establish a new identity
- Change becomes natural

---

### Journal Prompt: Your Emotional Baseline

*What emotions do you return to most often? What is your "normal" emotional state? Be honest—is it peace, anxiety, unworthiness, contentment, fear? This is your current set point.*

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

*Now, what emotional state would the person living your True Goal experience daily?*

_______________________________________________

_______________________________________________

_______________________________________________

---

### Emotional Alignment Practice

**Morning Practice (10 minutes):**

1. Close your eyes
2. Imagine you're living your True Goal fully realized
3. Feel into this reality—what emotions are present?
4. Let these emotions fill your entire body
5. Stay here for at least 2-5 minutes
6. Carry this emotional state into your day

**Throughout the Day:**

When you notice yourself falling into old emotional patterns, ask:
"Would the person I'm becoming feel this way?"

If not, consciously shift to your new emotional set point.

**Evening Practice (5 minutes):**

1. Review your day through the lens of your new identity
2. Celebrate moments when you maintained your new set point
3. Release any moments when you slipped back
4. Visualize yourself waking up tomorrow as the person you're becoming

> "The action you take, is the highest form of communication." — Breah Kettunen

### The Superconscious Visual Meditation Approach

One powerful way to create new emotional set points is through guided visualization that engages your superconscious mind. These are designed to:

- Create Magnetic Alignment with your True Goal
- Flip Negative Beliefs at their root
- Activate your Inner Power
- Enable Quantum Jumping to new realities
- Dissolve Conflicted Parts within you
- Open you to Total Financial Abundance
- Build Authentic Confidence
- Ignite your Internal Inner Fire

Each of these targets a specific emotional shift needed to become the person you desire to be.

> "You must feel worthy before wealth arrives. You must feel confident before success shows up. You must feel loved before love finds you. BE it before you SEE it." — Breah Kettunen

### Why This Works

When you change your emotional set point:
- Your decisions change
- Your actions change
- Your energy changes
- What you attract changes
- Your results change

And it happens naturally, without the struggle of willpower, because you're not fighting against yourself anymore. You've simply become someone for whom these new results are normal.

---

### Journal Space: Your New Emotional Set Point

*Describe in vivid detail the emotional state of the person you're becoming. How do they feel when they wake up? How do they move through challenges? What is their baseline emotional state?*

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

---

### Notes & Personal Insights

*Capture any breakthroughs from this chapter:*

_______________________________________________

_______________________________________________

_______________________________________________

---
    `,
    reflection: {
      prompt: "What is your current emotional set point? What emotions run in the background of your daily life? Now describe in detail the emotional set point of the person you're becoming—the one living your True Goal. What do they feel as they move through their day?",
      placeholder: "My current emotional set point is... The emotions I experience most often are... The person I'm becoming feels...",
    },
    keyTakeaways: [
      "You must BE it before you SEE it—emotional state precedes circumstances",
      "You cannot grow abundance from seeds of scarcity",
      "Create new emotional set points instead of trying to fix current ones",
      "Practice the emotional state of your achieved goal daily until it becomes your new normal",
    ],
  },
  {
    id: 4,
    step: "Step 4",
    title: "Unplug and Recode",
    subtitle: "Replace Old Programming with New Reality",
    duration: "55 min read",
    audioLength: "40 min",
    image: "https://images.unsplash.com/photo-1757353614837-d8085c4fdbd6?w=800&auto=format&fit=crop&q=80",
    summary: "Replace old habits, beliefs, and feelings with new ones and step into your new reality with a deep understanding of your limits. This is where you do the deep work of releasing the old programming.",
    content: `
## Unplug and Recode

The fourth step in the Magnetic Mind Method is to **Unplug and Recode**—replacing old habits, beliefs, and feelings with new ones so you can step into your new reality with a deep understanding of your limits.

This is where the transformation gets real. You're not just understanding concepts anymore—you're actively rewiring your unconscious programming.

> "The action you take, is the highest form of communication." — Breah Kettunen

### Understanding Resistance

Before we can recode, we need to understand what we're dealing with: **Resistance.**

Resistance shows up whenever you try to change. It's the force that:
- Makes you procrastinate on important actions
- Creates doubt just when you're about to breakthrough
- Brings up old fears and insecurities
- Pulls you back to familiar (uncomfortable) patterns
- Makes change feel impossible

**Resistance is not your enemy.** It's your unconscious mind trying to keep you safe. Remember: your unconscious learned how to "be" at an early age and doesn't want to change.

---

### Journal Prompt: Where Does Resistance Show Up?

*Where do you notice resistance appearing in your life? When you think about your True Goal, what fears or doubts arise? Write them down without judgment—just observe.*

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

---

### Where Resistance Comes From

Resistance comes from the tension between:
- Who you currently believe yourself to be
- Who you need to become to achieve your True Goal

Your unconscious has evidence that being the "old you" has kept you alive. It has no evidence that being the "new you" is safe.

So it resists. It's doing its job. But that job is no longer serving your highest good.

> "Commit your way to the All, to Source; trust in Him, and He will act." (Psalm 37:5)

### The Recode Process

**Step 1: Identify the Old Code**

What beliefs are running your current reality?

Look at your results. They're the perfect mirror of your unconscious programming.
- If you're broke, there's a money belief running
- If you're alone, there's a love/worth belief running
- If you're stuck, there's a limitation belief running
- If you're exhausted, there's a self-care belief running

Common old codes:
- "I'm not worthy of success"
- "Money is hard to get"
- "I always get hurt in relationships"
- "I can't handle success"
- "Who am I to want more?"
- "It's selfish to put myself first"
- "Something will go wrong"

---

### Journal Prompt: Identify Your Old Code

*What "old code" is running your current reality? Look at an area where you're stuck—what belief must be underneath it for that result to exist?*

_______________________________________________

_______________________________________________

_______________________________________________

_______________________________________________

---

**Step 2: Unplug the Old Code**

You cannot install new programming on top of old programming. You must first UNPLUG the old code.

As Jesus said in Matthew 9:17:
> *"Neither do men put new wine into old wineskins; else the skins burst, and the wine is spilled, and the skins perish: but they put new wine into fresh wineskins, and both are preserved."*

This ancient wisdom perfectly captures the Recode process—you cannot pour your new life into old containers. The old beliefs, patterns, and programming must be released first to make room for the new.

The Unplugging Process:

1. **Acknowledge** the old belief: "I notice I have a belief that..."
2. **Thank it** for trying to protect you: "This belief has kept me safe by..."
3. **Recognize** it's no longer needed: "I no longer need this protection because..."
4. **Release** it with gratitude: "I release this belief with love and gratitude"
5. **Create space** for the new

This isn't about fighting or forcing. Fighting gives the old belief more energy. Instead, you're simply acknowledging, thanking, and releasing.

**Step 3: Install the New Code**

Once you've created space, install the new programming:

1. **Choose the new belief** that supports your True Goal
2. **State it in present tense** as if it's already true
3. **FEEL it** in your body—emotion is the language of the unconscious
4. **Repeat** until it becomes automatic
5. **Act** as if the new belief is true

**Example:**
- Old code: "I'm not good enough to succeed"
- New code: "I am worthy of success simply because I exist"

The new code must be:
- In present tense ("I am" not "I will be")
- Emotionally charged
- Repeated consistently
- Supported by aligned action

### The Superconscious Recode Session

A Superconscious Recode Session is a guided process that a **certified Magnetic Mind Coach** takes you through, that helps you:

1. Access the unconscious level where old programming lives
2. Identify the specific belief or pattern ready to shift
3. Release it at its root (not just the surface symptom)
4. Install new programming aligned with your True Goal
5. Integrate the shift into your daily life

This is not just affirmation or positive thinking. It's working at the level where real change happens—the unconscious mind.

**Note:** You can also use one of my pre-recorded Recode sessions, meditations, or guided processes to experience this transformation on your own time.

If you would like a **live 1-on-1 personal Superconscious Recode session** with me, **email me at [takingyourpowerback@breahkettunen.com](mailto:takingyourpowerback@breahkettunen.com?subject=Recode%20Session)** or DM me on social media with the words **"Recode Session"** — I'll send you my personal Calendly link to schedule.

### Understanding Your Limits

An important part of recoding is developing a **deep understanding of your limits**—but perhaps not in the way you think.

Your limits are not real. They are beliefs masquerading as facts.

When you understand this deeply, you realize:
- "I can't" usually means "I believe I can't"
- "It's impossible" usually means "I've decided it's impossible"
- "That's not realistic" usually means "That's outside my current belief system"

**The only real limits are physical laws.** Everything else is negotiable.

### Releasing the Past

One of the biggest mistakes listed in the Magnetic Mind Method:
**"Holding on to the past as though it is real."**

Your past is not happening anymore. It only exists in your memory and your beliefs. When you hold onto past pain, failures, or trauma, you keep them alive in your present experience.

**You must let go of the past to create a new future.**

This doesn't mean pretending it didn't happen. It means:
- Processing the emotions fully
- Extracting any wisdom or lessons
- Releasing the emotional charge
- Choosing not to carry it forward
- Creating from NOW, not from history

### Taking Your Power Back: The Daily Power Practice

This is the heart of your transformation—a daily practice that walks you through all 5 steps of the Magnetic Mind Method each morning. We call it **The Superconscious Creator Code**.

When you commit to this practice, you're not just setting intentions—you're actively rewiring your unconscious mind and stepping into your role as the conscious creator of your life.

---

## THE SUPERCONSCIOUS CREATOR CODE

### The 4 Core Choices

Before you begin your daily practice, anchor yourself in these four foundational choices. These are the bedrock of your new identity:

1. **I choose to be the predominant creative force in my life**
2. **I choose to love my life**
3. **I choose health and vitality**
4. **I choose to live my true nature and purpose**

These four choices should be said and FELT every single day. They are non-negotiable declarations of who you are becoming.

> "But seek first the kingdom of Source and his righteousness, and all these things will be added to you." (Matthew 6:33)

---

### Journal Space: Your 4 Core Choices

*Write out the 4 Core Choices in your own words. Make them personal. Feel them as you write.*

**1. I choose to be the predominant creative force in my life because:**

_______________________________________________

_______________________________________________

**2. I choose to love my life, which means:**

_______________________________________________

_______________________________________________

**3. I choose health and vitality. To me this looks like:**

_______________________________________________

_______________________________________________

**4. I choose to live my true nature and purpose, which is:**

_______________________________________________

_______________________________________________

---

### Step 1: Choose Your True Goals

After the 4 Core Choices, add your specific True Goals using this format:

**"I choose the end result of _______________"**

Guidelines for choosing:
- Choose what you would LOVE, not what you think is realistic
- Choose the END RESULT, not the process
- Don't choose what someone else wants for you
- Don't choose something to "fix" yourself
- Choose something that excites you AND scares you a little
- Maximum of 12 focused choices at once (more can be overwhelming)

**Example True Goals:**
- "I choose the end result of financial freedom and abundance"
- "I choose the end result of a deeply loving partnership"
- "I choose the end result of vibrant health and energy"
- "I choose the end result of meaningful work that impacts thousands"

> "You can't plant seeds of scarcity and expect to get trees of abundance." — Christopher M Duncan

---

### Journal Space: Your True Goals

*Write your True Goals here. Remember: choose what you would LOVE.*

**1. I choose the end result of:**

_______________________________________________

**2. I choose the end result of:**

_______________________________________________

**3. I choose the end result of:**

_______________________________________________

**4. I choose the end result of:**

_______________________________________________

---

### Step 2: Create Structural Tension

Hold both realities simultaneously:

**Current Reality:** Where am I now? (Be honest, not dramatic)

_______________________________________________

_______________________________________________

**End Result:** Where am I going? (Your True Goal, clearly stated)

_______________________________________________

_______________________________________________

Feel the GAP between them. This gap creates the structural tension that pulls you forward. Don't try to escape current reality or over-inflate your vision—simply hold both clearly and let the tension work for you.

---

### Step 3: Emotion of the End Result

This is where the magic happens.

> "Commit your way to the All, to Source; trust in Him, and He will act." (Psalm 37:5)

**The Visualization Process:**

1. Close your eyes
2. Imagine your end result at the **back of your head** (this engages the superconscious)
3. See THREE PICTURES of your end result as if they've already happened
4. For each picture, **feel the emotions** as if it's real right now
5. Let gratitude, joy, and fulfillment fill your entire body
6. Stay here for at least 2-5 minutes

**The Three Pictures:**

_______________________________________________
*(Picture 1: What do you see? Feel it as real)*

_______________________________________________
*(Picture 2: What do you see? Feel it as real)*

_______________________________________________
*(Picture 3: What do you see? Feel it as real)*

Remember: You must BE it before you SEE it. Your emotional state NOW creates your reality LATER.

---

### Step 4: Recode Resistance

After your visualization, check in with yourself:

**"Is there any part of me that is unwilling to have my end result?"**

If YES:
1. Acknowledge the resistance without judgment
2. Take a deep breath and consciously LET IT GO
3. If resistance persists, use a Superconscious Recode Session

If NO:
- Wonderful! Move to Step 5

The resistance check is crucial. Any part of you that doesn't believe you can have your goal will sabotage your progress. Address it daily.

---

### Step 5: Take Aligned Action

Now translate your inner work into real-world movement.

**Weekly Power Planning (Do this once per week):**

Ask yourself: *"What COULD I do this week to move toward my End Result?"*
(Brainstorm all possibilities)

Then commit: *"What WILL I do this week to move toward my End Result?"*
(Choose your Weekly Commitments)

My Weekly Commitments:
1. _________________________________
2. _________________________________
3. _________________________________

---

**Daily Power Planning (Do this every morning):**

**What is my intention for today?**
_________________________________

**My Top 3 priorities today:**
1. _________________________________
2. _________________________________
3. _________________________________

**Where was I uncomfortable yesterday?**
(This reveals where growth is happening)
_________________________________

---

**Daily Power Record (Do this every evening):**

**Notes from today:**
_________________________________

**What am I grateful for?**
1. _________________________________
2. _________________________________
3. _________________________________

---

## YOUR COMPLETE MORNING POWER PRACTICE

Here's your daily routine in one flow (10-15 minutes):

**1. The 4 Core Choices** (Say with feeling)
- I choose to be the predominant creative force in my life
- I choose to love my life
- I choose health and vitality
- I choose to live my true nature and purpose

**2. Your True Goals** (State your end results)
- "I choose the end result of..."

**3. Structural Tension** (Hold both realities)
- Current Reality → End Result

**4. Emotion of End Result** (Visualize with feeling)
- Three pictures at the back of your head
- FEEL them as already real

**5. Recode Resistance** (Check and release)
- "Is there any part unwilling to have this?"
- Let it go or use a Recode Session

**6. Daily Power Planning** (Set your intention)
- Today's intention
- Top 3 priorities
- Review yesterday's growth edges

---

### Signs You're Successfully Practicing

- Old triggers don't affect you the same way
- New actions feel more natural, less forced
- You catch yourself thinking/feeling differently
- Results start shifting without massive effort
- Your "normal" feels different (and better)
- Other people notice you've changed
- You look forward to your morning practice
- You feel more in control of your life
- Resistance releases more quickly
- Your Weekly Commitments get completed with ease

> "You're not fixing a broken person. You're removing programming that was never really you in the first place. Beneath the old code, you've always been whole." — Breah Kettunen
    `,
    reflection: {
      prompt: "What old code has been running your life? Identify one core belief that's been holding you back, then write out the full Unplug and Recode process for this belief: acknowledge it, thank it, recognize you no longer need it, release it, and state your new code.",
      placeholder: "My old code is... I acknowledge this belief has shown up as... I thank it for protecting me by... I no longer need this because... I release this belief with love and gratitude. My new code is...",
    },
    keyTakeaways: [
      "Resistance is your unconscious trying to keep you safe—it's not your enemy",
      "You must UNPLUG old programming before installing new programming",
      "Your limits are beliefs masquerading as facts",
      "Release the past to create a new future—it only has power if you keep it alive",
    ],
  },
  {
    id: 5,
    step: "Step 5",
    title: "Take Aligned Action",
    subtitle: "Create Unparalleled Momentum",
    duration: "50 min read",
    audioLength: "38 min",
    image: "https://images.unsplash.com/photo-1770036245437-0fa51b540412?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjB3YWxraW5nJTIwcGF0aCUyMGpvdXJuZXklMjBmb3J3YXJkfGVufDB8fHx8MTc3MDg4NjgxM3ww&ixlib=rb-4.1.0&q=85",
    summary: "Align your actions with your True Goal to create unparalleled momentum and remove the fear of 'getting it wrong.' Our behaviour is the highest form of communication.",
    content: `
## Take Aligned Action

The fifth and final step in the Magnetic Mind Method is to **Take Aligned Action**—aligning your actions with your True Goal to create unparalleled momentum and remove the fear of "getting it wrong."

Here's the crucial understanding: **Our behaviour is the highest form of communication.**

What you DO communicates to your unconscious mind, to the universe, and to yourself who you truly are. All the inner work means nothing if it isn't expressed through action.

### The Problem with Most Action

Most people take action from the wrong place:
- From fear (trying to escape something)
- From their old identity (reinforcing old patterns)
- From willpower (exhausting and unsustainable)
- From "should" (other people's agendas)
- From the Oscillating Structure (which always snaps back)

This is why action often feels like struggle, and why results don't stick.

### Aligned Action vs. Forced Action

**Forced Action:**
- Feels like pushing a boulder uphill
- Requires constant willpower
- Comes from "I should" or "I have to"
- Creates exhaustion
- Results snap back once effort stops
- Is working from the Oscillating Structure

**Aligned Action:**
- Feels like flowing downstream
- Feels natural and inspired
- Comes from "I want to" and "I get to"
- Creates energy
- Results compound over time
- Is working from the Creative Structure

When you've done Steps 1-4 properly:
- Your True Goal is clear
- Your structure pulls you forward
- Your emotional set point is aligned
- Your old programming has been recoded

From THIS place, action becomes obvious, natural, and effective.

### Removing the Fear of "Getting It Wrong"

One of the biggest blocks to action is fear. Specifically:
- Fear of failure
- Fear of judgment
- Fear of making mistakes
- Fear of "getting it wrong"

This fear keeps people paralyzed, stuck in planning and preparation, never actually moving forward.

**Here's the truth: There is no "wrong" action when you're aligned with your True Goal.**

Every action is either:
1. A step forward, OR
2. Valuable feedback showing you what to adjust

You cannot fail at becoming yourself. You can only learn.

The fear of getting it wrong is just another old code that needs to be unplugged and recoded.

### What Aligned Action Looks Like

**Aligned action is:**
- Consistent with your True Goal
- Coming from your new identity
- Supported by your new emotional set point
- Free from old programming
- Based on what you love, not what you fear

**Before taking any action, ask:**
1. Is this moving me toward my True Goal?
2. Am I taking this action from my new identity or old patterns?
3. What would the person I'm becoming do here?
4. Am I acting from love or fear?

If the action isn't aligned, don't take it. Instead, reconnect with Steps 1-4 and find the aligned action.

### Creating Unparalleled Momentum

Momentum is created when:
- Actions are consistent (small daily actions > sporadic big efforts)
- Actions are aligned (quality > quantity)
- Results are celebrated (every win matters)
- "Failures" are learned from (feedback, not setbacks)
- Identity stays strong (you ARE the person taking these actions)

**The compound effect of aligned action:**
- Day 1: Small shift
- Week 1: Noticeable change
- Month 1: Significant transformation
- Month 6: Unrecognizable life
- Year 1: Living your True Goal

### The Daily Aligned Action Practice

**Morning:**
1. Connect with your True Goal
2. Feel your new emotional set point
3. Ask: "What one aligned action will I take today?"
4. Commit to that action

**Before Each Action:**
1. Pause
2. Ask: "Am I aligned?"
3. If yes, proceed with confidence
4. If no, realign first

**Evening:**
1. Celebrate actions taken
2. Note any actions from old patterns
3. Recommit to alignment
4. Set intention for tomorrow

### When Others Don't Understand Your Actions

As you take aligned action, some people won't understand. They may:
- Question your choices
- Try to pull you back to old patterns
- Express concern or criticism
- Not support your changes

Remember:
- Their reaction is about their beliefs, not your truth
- You don't need permission to live your True Goal
- Some relationships will evolve, some will fall away
- The right people will meet you in your new reality

### The Integration: Bringing It All Together

You now have the complete Magnetic Mind Method:

**Step 1: Choose a True Goal**
Know what you truly desire—aligned with your authentic self, not the 7 Focus Points.

**Step 2: Create Structural Tension**
Move from the Oscillating Structure to the Creative Structure, so you're pulled toward your goal.

**Step 3: Emotion of the End Result**
Create new emotional set points based on who you're becoming, not who you're trying to fix.

**Step 4: Unplug and Recode**
Release old programming and install new beliefs that support your True Goal.

**Step 5: Take Aligned Action**
Move with purpose and momentum, free from the fear of getting it wrong.

> "You are not stuck. You are not limited. You are not broken. You have simply been running old programs. Now you know how to consciously create. Go live the life you were born to live." — Breah Kettunen

### Your Commitment

Place your hand on your heart and speak these words:

*"I commit to being the predominant creative force in my life. I choose my True Goal. I create from the Creative Structure. I embody my new emotional set point. I release all programming that no longer serves me. I take aligned action with confidence and joy. I am the Conscious Creator of my own life."*

**This is your moment. This is your transformation. This is you Taking Your Power Back.**
    `,
    reflection: {
      prompt: "What aligned action have you been avoiding because of fear of 'getting it wrong'? What would the person you're becoming do? Write your commitment to taking aligned action and describe what unparalleled momentum will look like in your life.",
      placeholder: "The aligned action I've been avoiding is... The fear behind it is... The person I'm becoming would... I commit to...",
    },
    keyTakeaways: [
      "Our behaviour is the highest form of communication",
      "Aligned action flows from your new identity, not willpower",
      "There is no 'wrong' action—only steps forward or valuable feedback",
      "Consistent aligned action creates unparalleled momentum",
    ],
  },
];

export const testimonials = [
  {
    id: 1,
    name: "Sarah M.",
    role: "Entrepreneur",
    quote: "Understanding the 9 Unconscious Egoic Agendas was a game-changer. I finally saw why I kept sabotaging my success. The Magnetic Mind Method helped me break free.",
    avatar: "SM",
  },
  {
    id: 2,
    name: "Michael R.",
    role: "Business Owner",
    quote: "I was stuck in the Oscillating Structure for years—achieving then losing, achieving then losing. Learning to create proper Structural Tension changed everything.",
    avatar: "MR",
  },
  {
    id: 3,
    name: "Jennifer L.",
    role: "Coach",
    quote: "The concept of creating new emotional set points rather than trying to fix myself was revolutionary. I finally understand: BE it before you SEE it.",
    avatar: "JL",
  },
];

export const commonMistakes = [
  "Working from the wrong structure",
  "Giving your power away",
  "Trying to heal the current reality, instead you must let go",
  "Thinking you will change when you achieve a result (you must BE it before you SEE it)",
  "Out of emotional alignment with what you desire",
  "Not following natural laws",
  "Not going for what you love",
  "Not following through",
  "Holding on to the past as though it is real",
];

export default { courseInfo, courseIntro, fourPillars, chapters, testimonials, commonMistakes };
